import { Helmet } from '@dr.pogodin/react-helmet'
import {
    Alert,
    Box,
    Button,
    Link,
    MenuItem,
    TextField,
    Typography
} from '@mui/material'
import { Formik } from 'formik'
import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import LoadingScreen from '@/components/LoadingScreen'
import { useBranding } from '@/contexts/BrandingContext'
import * as endPoints from '@/endpoints'
import AccountPageShell from '@/features/allauth/components/AccountPageShell'
import {
    isSignupEnabled,
    readRefusal,
    refusalMessage,
    signupDisabledMessage
} from '@/features/allauth/pages/account/selfServeRules'
import {
    FLAVOUR_OPTIONS,
    FLAVOUR_VALUES
} from '@/features/dashboard/site-admin/orgFlavours'
import useAuth from '@/hooks/useAuth'
import axios from '@/lib/axios'

const validationSchema = Yup.object().shape({
    name: Yup.string().max(120).required('Organisation name is required'),
    flavour: Yup.string()
        .oneOf(FLAVOUR_VALUES, 'Choose one of the options')
        .required('Choose one of the options')
})

/**
 * The organisation-creation step (self-serve-org-signup). Reached by OrgAccessGuard for a
 * verified, org-less, non-partner-staff user in place of an empty dashboard. Collects name
 * and flavour ONLY — never a domain: the claimed domain is derived server-side from the
 * user's verified email (design decision 2), so asking here would wrongly imply it's a choice.
 */
const CreateOrganisation = () => {
    const navigate = useNavigate()
    const { user, refreshSession, logout } = useAuth()
    const { brand, isLoading: brandLoading } = useBranding()
    const [refusal, setRefusal] = useState(null)

    // Ship-dark-enable-deliberately: check the flag before rendering the form at all, so
    // a user reaching this step while self-serve signup is off sees a clear "not open"
    // message instead of a form that will 400 on submit. Wait for the branding fetch
    // rather than deciding on its transient false-while-loading default (see
    // SignupEnabledGuard, which does the same for the register/... routes).
    if (brandLoading) {
        return <LoadingScreen />
    }

    if (!isSignupEnabled(brand.self_serve_signup_enabled)) {
        const disabled = signupDisabledMessage()
        return (
            <>
                <Helmet>
                    <title>{disabled.title}</title>
                </Helmet>
                <AccountPageShell>
                    <Typography variant="h4" gutterBottom>
                        {disabled.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {disabled.body}
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body2">
                            <Link component={RouterLink} to="/contact">
                                Contact support
                            </Link>
                        </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => logout()}
                        >
                            Log out
                        </Link>
                    </Box>
                </AccountPageShell>
            </>
        )
    }

    return (
        <>
            <Helmet>
                <title>Create your organisation</title>
            </Helmet>
            <AccountPageShell>
                <Typography variant="h4" gutterBottom>
                    Set up your organisation
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    {user?.email
                        ? `You're signed in as ${user.email}. Give your organisation a name and tell us what kind of place it is, and we'll take it from there.`
                        : "Give your organisation a name and tell us what kind of place it is, and we'll take it from there."}
                </Typography>

                {refusal && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {refusal.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mb: refusal.showSupportLink ? 1 : 0 }}
                        >
                            {refusal.body}
                        </Typography>
                        {refusal.showSupportLink && (
                            <Typography variant="body2">
                                Still stuck?{' '}
                                <Link component={RouterLink} to="/contact">
                                    Contact support
                                </Link>
                                .
                            </Typography>
                        )}
                    </Alert>
                )}

                <Formik
                    initialValues={{ name: '', flavour: 'school' }}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        setRefusal(null)
                        try {
                            await axios.post(endPoints.ORG_SELF_SERVE, values)
                            // Re-fetch the session so user.org / activeOrg reflect the new
                            // org before we navigate — otherwise OrgAccessGuard would bounce
                            // us straight back here on the next render.
                            await refreshSession()
                            navigate('/setup', { replace: true })
                        } catch (err) {
                            setRefusal(refusalMessage(readRefusal(err)))
                        } finally {
                            setSubmitting(false)
                        }
                    }}
                >
                    {({
                        errors: formErrors,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        isSubmitting,
                        touched,
                        values
                    }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            <TextField
                                autoFocus
                                error={Boolean(touched.name && formErrors.name)}
                                fullWidth
                                helperText={touched.name && formErrors.name}
                                label="Organisation name"
                                margin="normal"
                                name="name"
                                autoComplete="organization"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.name}
                                variant="outlined"
                            />
                            <TextField
                                error={Boolean(
                                    touched.flavour && formErrors.flavour
                                )}
                                fullWidth
                                helperText={
                                    touched.flavour && formErrors.flavour
                                }
                                label="What kind of organisation is this?"
                                margin="normal"
                                name="flavour"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                select
                                value={values.flavour}
                                variant="outlined"
                            >
                                {FLAVOUR_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Box sx={{ mt: 3 }}>
                                <Button
                                    color="primary"
                                    disabled={isSubmitting}
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                >
                                    Create organisation
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>

                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => logout()}
                    >
                        Log out
                    </Link>
                </Box>
            </AccountPageShell>
        </>
    )
}

export default CreateOrganisation
